import { useFormik } from 'formik'
import { useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { pricingAPI } from '../api/services/pricing'
import { PricesList } from '../api/services/pricing/types.ts'

type FormValues = {
  price1m: number
  price3m: number
  price6m: number
  price12m: number
}

type DefaultPrices = {
  price1m: number
  price3m: number
  price6m: number
  price12m: number
}

export const usePriceListFormik = (
  userPrices: PricesList[],
  mainPrices: PricesList[],
  defaultPrices: DefaultPrices,
  setChangeStatus: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const [resetFormDone, setResetFormDone] = useState(false)

  const validationSchema = Yup.object({
    price1m: Yup.number()
      .required('To pole jest wymagane')
      .test(
        'max-price1m',
        `Kwota nie może być większa od domyślnej ${defaultPrices.price1m} PLN`, // Error message
        (value) => {
          // Perform custom validation logic
          return (
            mainPrices.length === 0 ||
            (value !== undefined && value <= defaultPrices.price1m)
          )
        }
      ),
    price3m: Yup.number()
      .required('To pole jest wymagane')
      .test(
        'max-price3m',
        `Kwota nie może być większa od domyślnej ${defaultPrices.price3m} PLN`, // Error message
        (value) => {
          // Perform custom validation logic
          return (
            mainPrices.length === 0 ||
            (value !== undefined && value <= defaultPrices.price3m)
          )
        }
      ),
    price6m: Yup.number()
      .required('To pole jest wymagane')
      .test(
        'max-price6m',
        `Kwota nie może być większa od domyślnej ${defaultPrices.price6m} PLN`, // Error message
        (value) => {
          // Perform custom validation logic
          return (
            mainPrices.length === 0 ||
            (value !== undefined && value <= defaultPrices.price6m)
          )
        }
      ),
    price12m: Yup.number()
      .required('To pole jest wymagane')
      .test(
        'max-price12m',
        `Kwota nie może być większa od domyślnej ${defaultPrices.price12m} PLN`, // Error message
        (value) => {
          // Perform custom validation logic
          return (
            mainPrices.length === 0 ||
            (value !== undefined && value <= defaultPrices.price12m)
          )
        }
      )
  })

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      price1m:
        userPrices.length > 0 ? Number(userPrices[0].month_diet_price) : 0,
      price3m:
        userPrices.length > 0
          ? Number(userPrices[0].three_month_diet_price)
          : 0,
      price6m:
        userPrices.length > 0 ? Number(userPrices[0].six_month_diet_price) : 0,
      price12m:
        userPrices.length > 0
          ? Number(userPrices[0].twelve_month_diet_price)
          : 0
    },
    validationSchema,
    onSubmit: async (values: FormValues) => {
      const newPrices = {
        month_diet_price: values.price1m.toString(),
        three_month_diet_price: values.price3m.toString(),
        six_month_diet_price: values.price6m.toString(),
        twelve_month_diet_price: values.price12m.toString()
      }
      const toastId = toast.loading('Proszę czekać.')
      try {
        const res = await pricingAPI.setPricing(newPrices)
        if (res.status === 200) {
          toast.update(toastId, {
            render: 'Zmiany zostały zapisane',
            type: 'success',
            isLoading: false,
            autoClose: 3000
          })
        }
        setChangeStatus(true)
      } catch (error) {
        toast.dismiss(toastId)
        console.error('Error updating prices:', error)
      }
    }
  })

  if (
    mainPrices.length > 0 &&
    userPrices.length > 0 &&
    formik.initialValues.price1m === 0 &&
    !resetFormDone
  ) {
    setResetFormDone(true)
    formik.resetForm({
      values: {
        price1m: Number(userPrices[0].month_diet_price),
        price3m: Number(userPrices[0].three_month_diet_price),
        price6m: Number(userPrices[0].six_month_diet_price),
        price12m: Number(userPrices[0].twelve_month_diet_price)
      },
      touched: {}, // Clear touched state if needed
      errors: {} // Clear errors if needed
    })
  }

  if (mainPrices.length === 0 && !resetFormDone) {
    setResetFormDone(true)
    formik.resetForm({
      values: defaultPrices,
      touched: {}, // Clear touched state if needed
      errors: {} // Clear errors if needed
    })
  }

  return formik
}
